<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { checkTruncationHappy } from '@/happyTheme/utils/product/checkTrunctationHappy'
import type { Product } from '@/storage/products'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { getProductName } from '@/utils/product/getProductName'
import { nextTick, onMounted, ref } from 'vue'

const props = defineProps<{
    product: Product
    description: string
}>()

const maxLines = ref<number>(1)
const descriptionElement = ref<HTMLElement>()
const divDescriptionElement = ref<HTMLElement>()
const buttonElementSelect = ref<HTMLElement>()

onMounted(() => {
    nextTick(() => {
        checkTruncationHappy(
            descriptionElement,
            divDescriptionElement,
            buttonElementSelect,
            maxLines
        )
        window.addEventListener('resize', () =>
            checkTruncationHappy(
                descriptionElement,
                divDescriptionElement,
                buttonElementSelect,
                maxLines
            )
        )
    })
})
</script>

<template>
    <section class="section-parent-card-product-happy">
        <section class="section-card-product-happy">
            <div class="div-card-product-happy">
                <img
                    class="img-card-product-happy"
                    :src="`${URL_IMAGE}${props.product.image}`"
                    :alt="getProductName(props.product)"
                />
            </div>
            <div class="div-description-card-product-happy" ref="divDescriptionElement">
                <section class="flex flex-col h-full w-full">
                    <div
                        class="div-description-child-card-product-happy"
                        ref="descriptionElement"
                    >
                        <h3 class="text-product-name-happy">
                            {{ getProductName(props.product) }}
                        </h3>
                        <p class="text-price-product-happy">
                            {{ convertPriceToEuro(props.product.price) + ' €' }}
                        </p>
                    </div>
                    <p
                        v-if="props.description !== 'Aucune description'"
                        class="text-description-product-happy"
                        :class="`line-clamp-${maxLines}`"
                        :style="{ '-webkit-line-clamp': maxLines }"
                    >
                        {{ props.description }}
                    </p>
                    <div
                        class="div-button-select-product-full-happy"
                        ref="buttonElementSelect"
                    >
                        Sélectionner
                    </div>
                </section>
            </div>
        </section>
    </section>
</template>

<style scoped>
.section-parent-card-product-happy {
    @apply w-full h-full flex justify-center items-center p-0.5;
}

.section-card-product-happy {
    @apply flex flex-col justify-end items-center min-h-52 w-full rounded-lg relative;
    @apply shadow happy-light:shadow-text-dark-happy-light happy-dark:shadow-bg-happy-light;
    @apply border-[1.5px] happy-light:border-text-dark-happy-light happy-dark:border-bg-happy-light;
    @apply overflow-hidden;
}
.section-card-product-happy:active {
    @apply bg-border-red-happy-light;
}
.div-card-product-happy {
    @apply absolute inset-0 min-w-full h-full z-0 flex pb-16;
}
.img-card-product-happy {
    @apply h-full w-full object-center object-contain;
}

.div-description-card-product-happy {
    @apply absolute w-full h-24 p-2 flex flex-col;
    @apply justify-start bottom-0 left-0 overflow-hidden z-10 gap-1.5;
    background-color: hsla(0, 0%, 20%, 0.7);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
}
.div-description-card-product-happy::before {
    content: '';
    @apply absolute inset-0 z-0;
}
.div-description-card-product-happy > * {
    @apply relative z-20;
}
.div-description-child-card-product-happy {
    @apply w-full flex justify-between items-start;
    @apply flex-shrink-0;
}

.text-product-name-happy {
    @apply text-sm font-semibold text-wrap text-bg-happy-light;
}
.text-price-product-happy {
    @apply text-sm font-semibold text-nowrap text-button-yellow-happy-light;
}
.text-description-product-happy {
    @apply text-xs text-ellipsis text-bg-happy-light;
    @apply flex-1 min-h-0 overflow-hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
}

.div-button-select-product-full-happy {
    @apply w-full h-8 bg-button-yellow-happy-light text-center text-sm;
    @apply font-bold text-slate-eronor flex justify-center items-center;
    @apply rounded-md mt-2 cursor-pointer shadow shadow-text-dark-happy-light;
}

@media (hover: hover) {
    .section-card-product-happy:hover {
        @apply bg-border-red-happy-light;
    }
}
</style>
